/**
 * Game-over scoreboard shown to ALL players (host + everyone).
 * Triggered by the host pressing "End Game & Reveal".
 */
const FinalScoreboard = ({
  scores,
  participants,
  roundNumber,
  roomCode,
  isHost,
  onResetGame,
  onCloseRoom,
  mode = "solo",
  teams = [],
  teamScores = {},
}) => {
  const isTeamsMode = mode === "teams";

  // ---- SOLO leaderboard rows ----
  const hostNames = new Set(participants.filter((p) => p.is_host).map((p) => p.name));
  const rows = Object.entries(scores || {})
    .filter(([name]) => !hostNames.has(name))
    .map(([name, score]) => ({ name, score }))
    .sort((a, b) => b.score - a.score);

  // ---- TEAM leaderboard rows (only in team mode) ----
  const teamRows = isTeamsMode
    ? teams
        .map((t) => ({
          id: t.id,
          name: t.name,
          color: t.color,
          score: teamScores[t.id] || 0,
          members: (participants || []).filter((p) => !p.is_host && p.team_id === t.id).map((p) => p.name),
        }))
        .sort((a, b) => b.score - a.score)
    : [];

  // The "leaderboard" the winner banner uses — in team mode it's teams, otherwise players
  const rankingRows = isTeamsMode ? teamRows : rows;
  const winnerScore = rankingRows[0]?.score ?? 0;
  const winners = rankingRows.filter((r) => r.score === winnerScore && winnerScore > 0);

  // Escape a value for CSV (RFC 4180): wrap in quotes, escape inner quotes
  const csvCell = (v) => {
    const s = String(v ?? "");
    return `"${s.replace(/"/g, '""')}"`;
  };

  const handleExportCSV = () => {
    const now = new Date();
    const dateStr = now.toLocaleString();
    const roundsPlayed = Math.max(0, (roundNumber || 1) - 1);

    // Build CSV: header metadata + blank line + score table
    const lines = [
      ["Tap First — Game Results"].map(csvCell).join(","),
      ["Room Code", roomCode || ""].map(csvCell).join(","),
      ["Date", dateStr].map(csvCell).join(","),
      ["Rounds Played", roundsPlayed].map(csvCell).join(","),
      ["Total Players", rows.length].map(csvCell).join(","),
      "",
      ["Rank", "Player", "Score"].map(csvCell).join(","),
      ...rows.map((r, idx) =>
        [idx + 1, r.name, r.score].map(csvCell).join(",")
      ),
    ];

    // Prepend BOM so Excel opens UTF-8 correctly
    const csv = "\uFEFF" + lines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const stamp = now
      .toISOString()
      .replace(/[:T]/g, "-")
      .replace(/\..+/, "");
    const filename = `tapfirst-${roomCode || "results"}-${stamp}.csv`;

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-black/85 backdrop-blur-md px-4 py-8 overflow-y-auto"
      data-testid="final-scoreboard"
    >
      {/* Floating confetti */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full animate-pulse"
            style={{
              background: ["#FF0055", "#00F0FF", "#FFEA00", "#9D4EDD"][i % 4],
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${1 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 w-full max-w-xl">
        <p
          className="text-center text-[#00F0FF] tracking-[0.3em] text-xs font-semibold mb-1"
        >
          GAME OVER
        </p>
        <h2
          className="text-center text-5xl sm:text-6xl text-white mb-2"
          style={{ fontFamily: "Anton, sans-serif" }}
        >
          FINAL SCORES
        </h2>
        <p className="text-center text-[#A1A1AA] text-sm mb-6">
          {roundNumber - 1} round{roundNumber - 1 !== 1 ? "s" : ""} played
        </p>

        {/* Winner banner */}
        {winners.length > 0 && (
          <div className="text-center mb-6 px-4 py-5 bg-gradient-to-r from-[#FFEA00]/10 via-[#FFEA00]/20 to-[#FFEA00]/10 border border-[#FFEA00]/40 rounded-2xl">
            <p className="text-[#FFEA00] text-sm tracking-widest font-bold">
              🏆 {winners.length > 1 ? "TIE" : isTeamsMode ? "WINNING TEAM" : "CHAMPION"}
            </p>
            <p
              className="text-3xl sm:text-5xl text-white mt-1 break-words"
              style={{ fontFamily: "Anton, sans-serif", textShadow: "0 0 30px rgba(255,234,0,0.6)" }}
              data-testid="final-winner-name"
            >
              {winners.map((w) => w.name).join(" • ")}
            </p>
            {/* In team mode, list the players on the winning team beneath the team name */}
            {isTeamsMode && winners.length === 1 && winners[0].members?.length > 0 && (
              <p className="text-white/80 text-sm mt-2" data-testid="final-winner-team-members">
                {winners[0].members.join(" · ")}
              </p>
            )}
            <p className="text-[#FFEA00] text-xl mt-1">
              {winnerScore} {winnerScore === 1 ? "point" : "points"}
            </p>
          </div>
        )}

        {/* Full scoreboard — teams first (if team mode), then individual as a smaller table */}
        <div className="bg-black/50 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden">
          <ul className="divide-y divide-white/5">
            {(isTeamsMode ? teamRows : rows).length === 0 ? (
              <li className="p-6 text-center text-[#A1A1AA]">No scores recorded</li>
            ) : isTeamsMode ? (
              teamRows.map((r, idx) => (
                <li
                  key={r.id}
                  className={`px-4 py-3 flex items-center gap-3 ${
                    idx === 0 && r.score > 0 ? "bg-[#FFEA00]/10" : ""
                  }`}
                  data-testid={`final-team-row-${r.id}`}
                >
                  <div className="w-8 text-center font-bold">
                    {idx === 0 && r.score > 0 ? (
                      <span className="text-[#FFD700] text-lg">🥇</span>
                    ) : idx === 1 ? (
                      <span className="text-[#C0C0C0] text-lg">🥈</span>
                    ) : idx === 2 ? (
                      <span className="text-[#CD7F32] text-lg">🥉</span>
                    ) : (
                      <span className="text-[#A1A1AA]">{idx + 1}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className="font-bold truncate"
                      style={{ color: r.color, fontFamily: "Anton, sans-serif", fontSize: "1.1rem" }}
                    >
                      {r.name}
                    </div>
                    {r.members.length > 0 && (
                      <div className="text-xs text-white/50 truncate">
                        {r.members.join(" · ")}
                      </div>
                    )}
                  </div>
                  <div
                    className="text-2xl font-bold tabular-nums"
                    style={{ fontFamily: "Anton, sans-serif", color: r.color }}
                  >
                    {r.score}
                  </div>
                </li>
              ))
            ) : (
              rows.map((r, idx) => (
                <li
                  key={r.name}
                  className={`px-4 py-3 flex items-center gap-3 ${
                    idx === 0 && r.score > 0 ? "bg-[#FFEA00]/10" : ""
                  }`}
                  data-testid={`final-row-${r.name}`}
                >
                  <div className="w-8 text-center font-bold">
                    {idx === 0 && r.score > 0 ? (
                      <span className="text-[#FFD700] text-lg">🥇</span>
                    ) : idx === 1 ? (
                      <span className="text-[#C0C0C0] text-lg">🥈</span>
                    ) : idx === 2 ? (
                      <span className="text-[#CD7F32] text-lg">🥉</span>
                    ) : (
                      <span className="text-[#A1A1AA]">{idx + 1}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-white font-semibold truncate">
                    {r.name}
                  </div>
                  <div
                    className="text-2xl text-[#00F0FF] font-bold tabular-nums"
                    style={{ fontFamily: "Anton, sans-serif" }}
                  >
                    {r.score}
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>

        {/* Export button — visible to everyone once the game is over */}
        {rows.length > 0 && (
          <div className="text-center mt-6">
            <button
              onClick={handleExportCSV}
              className="inline-flex items-center gap-2 px-5 py-3 bg-white/10 hover:bg-white/15 border border-white/20 rounded-full text-white font-semibold transition-all active:scale-95"
              data-testid="export-csv-button"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                />
              </svg>
              Download Results (Excel / CSV)
            </button>
          </div>
        )}

        {/* Host control */}
        {isHost && (
          <div className="text-center mt-4 flex flex-col items-center gap-3">
            <button
              onClick={onResetGame}
              className="px-6 py-3 bg-[#00F0FF] text-black font-bold rounded-full hover:bg-[#33F3FF] transition-all active:scale-95 shadow-[0_0_20px_rgba(0,240,255,0.5)]"
              data-testid="play-again-button"
            >
              ↺ Play Again (Reset Scores)
            </button>
            {onCloseRoom && (
              <button
                onClick={onCloseRoom}
                className="px-6 py-3 bg-[#FF0055] text-white font-bold rounded-full hover:bg-[#FF3377] transition-all active:scale-95 shadow-[0_0_20px_rgba(255,0,85,0.4)]"
                data-testid="final-close-room-button"
              >
                ✕ Close Room
              </button>
            )}
          </div>
        )}
        {!isHost && (
          <p className="text-center mt-4 text-[#A1A1AA] text-sm">
            Waiting for host to start a new game…
          </p>
        )}
      </div>
    </div>
  );
};

export default FinalScoreboard;
